import React from 'react';
import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme, useMediaQuery } from '@mui/material';

/**
 * EdgeSheet — secondary content panel (filters, detail preview, config).
 *
 * Behaviour by screen size:
 *
 *   md+  (desktop / tablet-landscape)
 *        Inline panel pushed alongside the main content.
 *        `anchor="right"` (default) → right-side panel with a fixed width.
 *        `anchor="left"`            → left-side panel.
 *        `anchor="bottom"`          → bottom panel with a fixed height.
 *        Does NOT overlay — it shifts the layout.
 *
 *   xs/sm (phone)
 *        Collapses into a SwipeableDrawer on the same edge, overlaying content.
 *        iOS-style momentum scrolling is enabled inside the sheet.
 *
 * Props:
 *   open         {boolean}   — controlled open state (required)
 *   onOpen       {function}  — called when the sheet should open (swipe / backdrop)
 *   onClose      {function}  — called when the sheet should close
 *   anchor       {string}    — 'right' (default) | 'left' | 'bottom'
 *   width        {number|string} — panel width for left/right anchor (default 320)
 *   height       {number|string} — panel height for bottom anchor (default '50vh')
 *   title        {string}    — optional header title rendered inside the sheet
 *   children     {node}
 *   sx           {object}    — extra sx on the inner content box
 *
 * Usage:
 *   const [filtersOpen, setFiltersOpen] = useState(false);
 *
 *   <Box sx={{ display: 'flex', flex: '1 1 auto', minHeight: 0 }}>
 *     <PageContent fill sx={{ flex: '1 1 auto' }}>
 *       <ResponsiveDataGrid ... />
 *     </PageContent>
 *
 *     <EdgeSheet
 *       open={filtersOpen}
 *       onOpen={() => setFiltersOpen(true)}
 *       onClose={() => setFiltersOpen(false)}
 *       title="Filters"
 *     >
 *       ... filter controls ...
 *     </EdgeSheet>
 *   </Box>
 */
export default function EdgeSheet({
  open,
  onOpen,
  onClose,
  anchor = 'right',
  width = 320,
  height = '50vh',
  title,
  children,
  sx,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isHorizontal = anchor === 'left' || anchor === 'right';

  const sheetContent = (
    <Box
      sx={{
        width: isHorizontal ? width : '100%',
        height: !isHorizontal ? height : '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // iOS momentum scroll inside the sheet
        WebkitOverflowScrolling: 'touch',
        ...sx,
      }}
    >
      {/* Optional header */}
      {(title || isMobile) && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              flexShrink: 0,
            }}
          >
            {title && (
              <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                {title}
              </Typography>
            )}
            {isMobile && (
              <IconButton size="small" onClick={onClose} sx={{ ml: 'auto' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Divider />
        </>
      )}

      {/* Scrollable body */}
      <Box sx={{ flex: '1 1 auto', overflowY: 'auto', p: 2 }}>
        {children}
      </Box>
    </Box>
  );

  // Mobile: overlay SwipeableDrawer
  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor={anchor}
        open={open}
        onOpen={onOpen ?? (() => {})}
        onClose={onClose}
        disableBackdropTransition={false}
        disableDiscovery={false}
        // Give the sheet safe-area clearance on iOS
        PaperProps={{
          sx: {
            pb: `env(safe-area-inset-bottom, 0px)`,
          },
        }}
      >
        {sheetContent}
      </SwipeableDrawer>
    );
  }

  // Desktop: inline panel — only rendered when open
  if (!open) return null;

  return (
    <Box
      sx={{
        flexShrink: 0,
        borderLeft: anchor === 'right'
          ? `1px solid ${theme.palette.divider}`
          : undefined,
        borderRight: anchor === 'left'
          ? `1px solid ${theme.palette.divider}`
          : undefined,
        borderTop: anchor === 'bottom'
          ? `1px solid ${theme.palette.divider}`
          : undefined,
        overflow: 'hidden',
      }}
    >
      {sheetContent}
    </Box>
  );
}
